// The one shared detail modal used by every step (app.md §8). Both modes mirror
// the same layout: a sticky action bar on top (title/status + the step's
// actions + close), PDF beside the invoice summary, line items full-width below.
// View-only everywhere except Import Control, whose editable form owns its own
// action bar (Save / Save & Release). The PDF pane shows only where the profile
// allows document access (`canPreviewPdf`) — which, since 2026-08-10, includes a
// company approver at any of the three tiers (security.md §5, owner ruling R2).
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  Divider,
  Spinner,
  Subtitle2,
  Caption1,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { DismissRegular } from '@fluentui/react-icons'
import type { AssociatedEntity, InvoiceLineItem, CurrentUser, Decision, Flag, Invoice, Upload } from '../lib/types'
import {
  applyLineItemAccountEdit,
  applyLineItemCostCenterEdit,
  applyLineItemCostCentersBulk,
  applyLineItemExpenseTypeEdit,
  getInvoiceBundle,
  getUpload,
  loadLineItems,
} from '../lib/data'
import { errorMessage } from '../api/errors'
import { trackOverlayVars } from '../lib/overlay'
import { InvoiceHeaderPanel } from './InvoiceHeaderPanel'
import { InvoiceLineItemsTable } from './InvoiceLineItemsTable'
import { AccountsSummary } from './AccountsSummary'
import { toNumber } from '../lib/format'
import { DecisionHistory } from './DecisionHistory'
import { PdfPane } from './PdfPane'
import { EditableInvoiceForm } from './EditableInvoiceForm'
import { CommentThread } from './CommentThread'

const useStyles = makeStyles({
  // The surface overlays the page's content area: top just under the header's
  // blue line, bottom near the viewport edge, width matching the page content
  // box. App.tsx tags the anchors; lib/overlay.ts measures the live layout into
  // the --ip-overlay-* CSS variables (fallbacks approximate the same geometry
  // before they're set). The 8px insets keep the overlay clearly in frame
  // rather than pixel-flush (user preference).
  surface: {
    position: 'fixed',
    top: 'calc(var(--ip-overlay-top, 72px) + 8px)',
    bottom: '8px',
    left: 'var(--ip-overlay-left, 3vw)',
    width: 'var(--ip-overlay-width, 94vw)',
    maxWidth: 'none',
    // Fluent's surface defaults to height: fit-content, which ignores the
    // top/bottom anchors and overflows the viewport — auto restores anchoring.
    height: 'auto',
    maxHeight: 'none',
    margin: '0',
    paddingRight: '12px',
  },
  // Fill the now fixed-height surface; inner panes own the scrolling.
  body: { height: '100%', maxHeight: 'none' },
  // One scroll container per mode; the sticky action bar rides its top.
  scroll: {
    height: '100%',
    overflowY: 'auto',
    paddingRight: '8px',
  },
  view: { display: 'flex', flexDirection: 'column', gap: '16px' },
  // Mirrors EditableInvoiceForm's action bar: sticky, blue rule underneath.
  actionBar: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `3px solid ${tokens.colorBrandStroke1}`,
  },
  actionTitle: { flex: 1, minWidth: 0 },
  // PDF + invoice summary side-by-side on top; line items span full width below
  // (same shape as the Import Control editor).
  topRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
  },
  pdfWrap: { height: '440px', minHeight: 0, minWidth: 0, '@media (max-width: 900px)': { height: '300px' } },
  headerCol: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
  editError: { color: tokens.colorPaletteRedForeground1 },
})

interface Props {
  open: boolean
  invoice: Invoice | null
  showPdf: boolean
  editable?: boolean
  /** Unlock only the per-line-item GL account picker on the otherwise read-only
      view (planning §3 — GL editable at every approval step + Export Control).
      Distinct from `editable` (the full Import Control editor). A pick asks the
      API to apply the account; it checks the caller's authority for the step,
      stamps the account as manually set, writes the correction row and returns
      the stored line, and the line items reload so the Accounts Summary and the
      reconcile footer follow. */
  /** Export Control and Manual Entry: the COMPOSED account picker. */
  editAccounts?: boolean
  /** The three approval tiers: the "What is it?" half-write. Separate from
      `editCostCenters` because the two are separate grants and separate routes —
      Export Control and Manual Entry hold the cost center without this one. */
  editExpenseTypes?: boolean
  /** The three approval tiers, Export Control and Manual Entry: the "Who is it
      for?" half-write. */
  editCostCenters?: boolean
  user: CurrentUser
  onClose: () => void
  onChanged?: () => void
  /** Called after a successful release (editable mode). Falls back to onChanged. */
  onReleased?: () => void
  /** Called after an archive as not-an-invoice (editable mode). Falls back to onReleased/onChanged. */
  onArchived?: () => void
  /** View-mode action buttons (Approve/Deny/etc.), rendered in the top action bar. */
  actions?: React.ReactNode
}

export function InvoiceDetailModal({
  open,
  invoice,
  showPdf,
  editable,
  editAccounts,
  editExpenseTypes,
  editCostCenters,
  user,
  onClose,
  onChanged,
  onReleased,
  onArchived,
  actions,
}: Props) {
  const s = useStyles()
  const [lineItems, setLineItems] = useState<InvoiceLineItem[] | 'loading'>('loading')
  const [savingLineItemId, setSavingLineItemId] = useState<string | undefined>(undefined)
  const [accountError, setAccountError] = useState<string | null>(null)
  const [flags, setFlags] = useState<Flag[]>([])
  const [decisions, setDecisions] = useState<Decision[] | 'loading'>('loading')
  const [upload, setUpload] = useState<Upload | undefined | 'loading'>('loading')
  // Tagged entities per line item (AE4) — read-only display for approvers. Loaded
  // lazily and best-effort; a failure just leaves the map empty (no chips).
  const [entitiesByLineItem, setEntitiesByLineItem] = useState<Record<string, AssociatedEntity[]>>({})
  // Monotonic request id: only the latest load may write state, so a slow
  // response for a just-closed invoice can't overwrite the one now open.
  const loadSeq = useRef(0)

  // Full reload: reseeds every pane, dropping line items into 'loading' (which
  // unmounts the editable form so it reseeds on remount). For the initial open
  // and invoice switch only — never after a save (that would discard edits).
  // One round trip: the header plus whichever satellites this mode shows. The
  // read-only path also takes the per-line tagged entities (the editable form
  // owns its own entity editor and loads them per line).
  const fullReload = useCallback(async () => {
    const iid = invoice?.id
    const seq = ++loadSeq.current
    if (!iid) return
    setLineItems('loading')
    setDecisions('loading')
    setFlags([])
    setEntitiesByLineItem({})
    try {
      const bundle = await getInvoiceBundle(
        iid,
        editable
          ? ['line_items', 'flags', 'decisions']
          : ['line_items', 'flags', 'decisions', 'associated_entities'],
      )
      if (seq !== loadSeq.current) return
      setLineItems(bundle.line_items ?? [])
      setFlags(bundle.flags ?? [])
      setDecisions(bundle.decisions ?? [])
      const grouped: Record<string, AssociatedEntity[]> = {}
      for (const e of bundle.associated_entities ?? []) {
        const k = e.child_id ?? ''
        if (k) (grouped[k] ??= []).push(e)
      }
      setEntitiesByLineItem(grouped)
    } catch {
      if (seq !== loadSeq.current) return
      setLineItems([])
      setFlags([])
      setDecisions([])
    }
  }, [invoice?.id, editable])

  // Light reload: refreshes flags + decisions WITHOUT touching line items, so the
  // editable form stays mounted and its state (just persisted, or still being
  // edited) survives. Used after a save / mark-resolved.
  const lightReload = useCallback(async () => {
    const iid = invoice?.id
    if (!iid) return
    const seq = loadSeq.current
    const bundle = await getInvoiceBundle(iid, ['flags', 'decisions'])
    if (seq !== loadSeq.current) return
    setFlags(bundle.flags ?? [])
    setDecisions(bundle.decisions ?? [])
  }, [invoice?.id])

  // GL account edit from a read-only approval view (planning §3). One call, and
  // the authority is the API's business: it checks the caller's standing at the
  // record's current step, applies the account with manual provenance and writes
  // the correction row. It answers `{ applied: false, reason }` on a refusal
  // rather than throwing, so a refusal reads as the API's own wording.
  const handleAccountChange = useCallback(
    async (lineItem: InvoiceLineItem, accountId: string) => {
      const iid = invoice?.id
      if (!iid) return
      // Capture the load id BEFORE the write, not after it: fullReload bumps
      // loadSeq when the reviewer switches invoices, and a seq read after the
      // await would always match, letting this invoice's line items land in the
      // one now open (same guard shape as the open effect).
      const seq = loadSeq.current
      setAccountError(null)
      setSavingLineItemId(lineItem.id)
      try {
        const { applied, reason } = await applyLineItemAccountEdit(
          iid,
          lineItem.id,
          accountId,
          lineItem.row_version,
        )
        if (!applied) {
          setAccountError(
            reason ? `The GL account change was rejected: ${reason}` : 'The GL account change was rejected.',
          )
          // Re-read on a refusal, same reason as the expense-type handler below:
          // the picker is showing the option the reviewer chose and the record
          // is not.
          const reverted = await loadLineItems(iid)
          if (seq === loadSeq.current) setLineItems(reverted)
          return
        }
        const fresh = await loadLineItems(iid)
        if (seq === loadSeq.current) setLineItems(fresh) // else a different invoice is open now
        onChanged?.()
      } catch (e) {
        setAccountError(`Couldn't save the GL account: ${errorMessage(e)}`)
      } finally {
        setSavingLineItemId(undefined)
      }
    },
    [invoice?.id, onChanged],
  )
  // "What is it?" — the expense-type half-write. Same shape as the two beside
  // it, and separate from them because it is a separate route with its own
  // authority: the server re-applies the composition rule to the cost center on
  // an expense-type change (D10), which is why the line items are reloaded rather
  // than patched in place.
  const handleExpenseTypeChange = useCallback(
    async (lineItem: InvoiceLineItem, expenseTypeCode: string) => {
      const iid = invoice?.id
      if (!iid) return
      const seq = loadSeq.current
      setAccountError(null)
      setSavingLineItemId(lineItem.id)
      try {
        const { applied, reason } = await applyLineItemExpenseTypeEdit(
          iid,
          lineItem.id,
          expenseTypeCode,
          lineItem.row_version,
        )
        if (!applied) {
          setAccountError(
            reason ? `The expense type change was rejected: ${reason}` : 'The expense type change was rejected.',
          )
          // Re-read even on a refusal. The row is unchanged on the server, but
          // the picker is showing the option the reviewer just chose, and
          // leaving it there means the screen states an expense type the record
          // does not have — beside a cost center that was never cleared, since
          // nothing was written. Snapping the table back to stored values makes
          // the banner the only thing that changed.
          const reverted = await loadLineItems(iid)
          if (seq === loadSeq.current) setLineItems(reverted)
          return
        }
        const fresh = await loadLineItems(iid)
        if (seq === loadSeq.current) setLineItems(fresh) // else a different invoice is open now
        onChanged?.()
      } catch (e) {
        setAccountError(`Couldn't save the expense type: ${errorMessage(e)}`)
      } finally {
        setSavingLineItemId(undefined)
      }
    },
    [invoice?.id, onChanged],
  )
  const handleCostCenterChange = useCallback(
    async (lineItem: InvoiceLineItem, costCenterCode: string) => {
      const iid = invoice?.id
      if (!iid) return
      // Capture the load id BEFORE the write, not after it: fullReload bumps
      // loadSeq when the reviewer switches invoices, and a seq read after the
      // await would always match, letting this invoice's line items land in the
      // one now open (same guard shape as the open effect).
      const seq = loadSeq.current
      setAccountError(null)
      setSavingLineItemId(lineItem.id)
      try {
        const { applied, reason } = await applyLineItemCostCenterEdit(
          iid,
          lineItem.id,
          costCenterCode,
          lineItem.row_version,
        )
        if (!applied) {
          setAccountError(
            reason ? `The cost center change was rejected: ${reason}` : 'The cost center change was rejected.',
          )
          // Re-read on a refusal, same reason as the two handlers above.
          const reverted = await loadLineItems(iid)
          if (seq === loadSeq.current) setLineItems(reverted)
          return
        }
        const fresh = await loadLineItems(iid)
        if (seq === loadSeq.current) setLineItems(fresh) // else a different invoice is open now
        onChanged?.()
      } catch (e) {
        setAccountError(`Couldn't save the GL account: ${errorMessage(e)}`)
      } finally {
        setSavingLineItemId(undefined)
      }
    },
    [invoice?.id, onChanged],
  )
  // §9.3 — BULK "Who is it for?". One cost center across the selected lines, in
  // one server transaction, so a twelve-line invoice is one act rather than
  // twelve. It refuses all-or-nothing — a stale `row_version` on any line, a
  // line that does not compose, a line that already has an answer — and the
  // table surfaces the server's own wording, offending line numbers included.
  const handleBulkCostCenter = useCallback(
    async (selection: InvoiceLineItem[], costCenterCode: string) => {
      const iid = invoice?.id
      if (!iid) return
      const seq = loadSeq.current
      setAccountError(null)
      const { applied, reason, lineNumbers } = await applyLineItemCostCentersBulk(
        iid,
        selection,
        costCenterCode,
      )
      if (!applied) {
        const lines =
          lineNumbers && lineNumbers.length > 0 ? ` (line${lineNumbers.length > 1 ? 's' : ''} ${lineNumbers.join(', ')})` : ''
        // Thrown, not returned: the table's Apply button owns this message, so
        // it renders beside the control the reviewer just used.
        throw new Error(`${reason ?? 'The change was rejected.'}${lines}`)
      }
      const fresh = await loadLineItems(iid)
      if (seq === loadSeq.current) setLineItems(fresh) // else a different invoice is open now
      onChanged?.()
    },
    [invoice?.id, onChanged],
  )

  // Measure the overlay geometry as the modal opens (before first paint) and
  // keep it fresh while open — see lib/overlay.ts. Also lock the page scroll:
  // the overlay is position:fixed against measured anchors, so background
  // scrolling shifts its contents — and there's nothing to scroll to while a
  // modal is up anyway. (The re-measure-on-scroll tracking stays, as a belt
  // for scrolls we can't lock, e.g. the host page outside the app frame.)
  useLayoutEffect(() => {
    if (!open) return
    const html = document.documentElement
    const body = document.body
    const prevHtmlOverflow = html.style.overflow
    const prevBodyOverflow = body.style.overflow
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    const untrack = trackOverlayVars()
    return () => {
      html.style.overflow = prevHtmlOverflow
      body.style.overflow = prevBodyOverflow
      untrack()
    }
  }, [open])

  useEffect(() => {
    if (!open || !invoice) return
    fullReload() // bumps loadSeq synchronously; the upload fetch shares that id
    const seq = loadSeq.current
    if (showPdf && invoice.upload_id) {
      setUpload('loading')
      getUpload(invoice.upload_id)
        .then((u) => { if (seq === loadSeq.current) setUpload(u) })
        .catch(() => { if (seq === loadSeq.current) setUpload(undefined) })
    } else {
      setUpload(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id])

  if (!invoice) return null

  const pdfPane = (
    <PdfPane
      upload={upload}
      pageStart={invoice.upload_page_start ?? undefined}
      pageEnd={invoice.upload_page_end ?? undefined}
    />
  )

  const body = editable ? (
    lineItems === 'loading' ? (
      // Mount the form only once line items are loaded — it seeds its editable row
      // state once on mount, so mounting with [] would leave it empty.
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner label="Loading line items…" />
      </div>
    ) : (
      <div className={s.scroll}>
        <EditableInvoiceForm
          key={invoice.id}
          invoice={invoice}
          lineItems={lineItems}
          flags={flags}
          pdf={showPdf ? pdfPane : null}
          onClose={onClose}
          onChanged={() => {
            lightReload()
            onChanged?.()
          }}
          onReleased={() => {
            ;(onReleased ?? onChanged)?.()
            onClose()
          }}
          onArchived={() => {
            ;(onArchived ?? onReleased ?? onChanged)?.()
            onClose()
          }}
        />
        {invoice.id && (
          <div style={{ marginTop: 16 }}>
            <Divider style={{ marginBottom: 16 }} />
            <CommentThread parentId={invoice.id} user={user} />
          </div>
        )}
      </div>
    )
  ) : (
    <div className={mergeClasses(s.scroll, s.view)}>
      <div className={s.actionBar}>
        <Subtitle2 className={s.actionTitle}>
          {invoice.vendor_name ?? 'Invoice'} · {invoice.external_invoice_id ?? invoice.id}
        </Subtitle2>
        {actions}
        <Button appearance="subtle" icon={<DismissRegular />} aria-label="Close" onClick={onClose} />
      </div>
      <div className={s.topRow} style={showPdf ? undefined : { display: 'block' }}>
        {showPdf && <div className={s.pdfWrap}>{pdfPane}</div>}
        <div className={s.headerCol}>
          <Subtitle2>Invoice Summary</Subtitle2>
          <InvoiceHeaderPanel invoice={invoice} />
        </div>
      </div>
      <Divider />
      <Subtitle2>Line Items Summary</Subtitle2>
      {accountError && <Caption1 className={s.editError}>{accountError}</Caption1>}
      <InvoiceLineItemsTable
        lineItems={lineItems}
        subtotal={invoice.subtotal}
        tax={invoice.tax}
        total={invoice.total}
        invoiceCompanyId={invoice.company_id}
        entitiesByLineItem={entitiesByLineItem}
        onAccountChange={editAccounts ? handleAccountChange : undefined}
        onExpenseTypeChange={editExpenseTypes ? handleExpenseTypeChange : undefined}
        onCostCenterChange={editCostCenters ? handleCostCenterChange : undefined}
        onBulkCostCenter={editCostCenters ? handleBulkCostCenter : undefined}
        savingLineItemId={savingLineItemId}
      />
      {lineItems !== 'loading' && (
        <AccountsSummary
          recordCompanyId={invoice.company_id}
          rows={lineItems.map((c) => ({
            accountId: c.final_account_id,
            accountName: c.account_name,
            expenseTypeCode: c.expense_type_code,
            companyId: c.company_id,
            companyName: c.company_name,
            total: toNumber(c.total),
          }))}
        />
      )}
      {invoice.id && (
        <>
          <Divider />
          <CommentThread parentId={invoice.id} user={user} />
        </>
      )}
      <Divider />
      <Subtitle2>Decision History</Subtitle2>
      <DecisionHistory decisions={decisions} />
    </div>
  )

  // Don't mount the focus-trapping Dialog until the editable form's data is
  // ready. If the form mounts LATE (cold open, line items still loading), Fluent's
  // Dialog auto-focus re-fires when the form appears and yanks focus to the
  // first action-bar button — stealing it from the field the reviewer just
  // clicked (the cold-first-open focus-steal; confirmed via the focus log:
  // input → button.fui-Button ~100ms after the click). Opening only once the
  // content is present makes Fluent's initial focus run exactly once. View mode
  // is unaffected (contentReady is always true there; its panes self-spinner).
  const contentReady = !editable || lineItems !== 'loading'

  return (
    <>
      {open && editable && !contentReady && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spinner label="Loading invoice…" />
        </div>
      )}
      <Dialog open={open && contentReady} onOpenChange={(_, d) => { if (!d.open) onClose() }}>
      <DialogSurface className={s.surface}>
        <DialogBody className={s.body}>
          {/* Both modes carry their own sticky action bar (title + actions +
              close), so there is no DialogTitle row. */}
          <DialogContent>{body}</DialogContent>
        </DialogBody>
      </DialogSurface>
      </Dialog>
    </>
  )
}
